import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Download, Sparkles,  Pencil, X, ChevronDown, ThumbsDown } from 'lucide-react';
import type { GenConfig, GeneratedResult, FlowModel } from '../../types';
import { getProxyUrl } from './utils/proxyUrl';
import { useReferenceData } from './hooks/useReferenceData';
import { fetchFlowModels } from '../../services/api';
import { useStamps } from './hooks/useStamps';
import { useDrawCanvas } from './hooks/useDrawCanvas';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useToast } from '../../hooks/useToast';
import { useImageBlobPrefetch, fetchImageBlob } from '../../hooks/useImageBlobPrefetch';
import { saveImage, canSaveToPhotos } from '../../utils/saveImage';
import { drawShapeOnCanvas } from './utils/stampShapes';

import QuickGenChatPanel from './components/QuickGenChatPanel';
import GeneratingOverlay from './components/GeneratingOverlay';
import CaptionPanel from './components/CaptionPanel';
import SettingsCard, { type EditDraft } from './components/SettingsCard';
import ImageHistoryStrip from './components/ImageHistoryStrip';
import StampToolbar from './components/StampToolbar';
import StampOverlay from './components/StampOverlay';
import DrawCanvas from './components/DrawCanvas';
import type {
    Mode2Result as Mode2ResultType,
    Mode2RestoredInfo,
    Mode2ChatRestore,
} from './hooks/useMode2Generate';

interface Step4ResultProps {
    genConfig: GenConfig;
    generatedResult: GeneratedResult | null;
    mode2Result: Mode2ResultType | null;
    onBack: () => void;
    globalProfile: string | null;
    name: string;
    title?: string | null;
    organizationName?: string | null;
    organizationType: '商家' | '里';
    onContinueToMode3?: (feedbackText: string) => void;
    mode3InitialMessage?: string;
    mode3InitialImageFile?: File | null;
    onRegenerateWithConfig?: (updates: Partial<GenConfig>) => void;
    onRegenerateWithImage?: (imageFile: File, updates: Partial<GenConfig>) => void;
    onRegenerateWithCurrentImage?: (updates: Partial<GenConfig>) => void;
    isMode2Generating?: boolean;
    isMode2GenerationFailed?: boolean;
    isMode2V2?: boolean;
    flowModel: FlowModel;
    imageHistory?: Mode2ResultType[];
    selectedHistoryIndex?: number;
    onSelectHistoryImage?: (index: number) => void;
    /** 非 null 表示畫面內容來自圖片庫還原（而不是剛生成的結果） */
    restoredInfo?: Mode2RestoredInfo | null;
    /** 還原時要重建的對話內容 */
    chatRestore?: Mode2ChatRestore | null;
    /** 目前這段流程的生成紀錄 id */
    generationSessionId?: string;
    /** 對話 id 產生時回報給上層 */
    onChatSessionIdChange?: (chatSessionId: string) => void;
}

const makeEditDraft = (genConfig: GenConfig, _flowModel?: FlowModel): EditDraft => ({ // eslint-disable-line @typescript-eslint/no-unused-vars
    flowModelId: genConfig.flowModelId,
    answers: { ...genConfig.answers },
    additionalRequirements: genConfig.additionalRequirements || '',
    imageFiles: genConfig.imageAnswers ? { ...genConfig.imageAnswers } : {},
});


const Step4Result: React.FC<Step4ResultProps> = ({
    genConfig, mode2Result,
    name, title, organizationName, organizationType,
    onRegenerateWithConfig, onRegenerateWithCurrentImage, isMode2Generating, isMode2GenerationFailed, isMode2V2,
    flowModel,
    imageHistory = [],
    selectedHistoryIndex = -1,
    onSelectHistoryImage,
    restoredInfo = null,
    chatRestore = null,
    generationSessionId,
    onChatSessionIdChange,
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { showToast } = useToast();
    // 結果顯示期間就把原圖備妥，讓儲存與合成都能省掉一次網路往返
    const takeOriginalBlob = useImageBlobPrefetch(mode2Result?.imageUrl);
    const [isDislikeOpen, setIsDislikeOpen] = useState(false);
    const [dislikeReason, setDislikeReason] = useState('');
    const previewRef = useRef<HTMLDivElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const previewAreaRef = useRef<HTMLDivElement>(null);
    // 量測 flex-1 預覽區的實際可用尺寸，讓圖片以最長邊貼齊（避免直幅圖被切）
    const [previewBox, setPreviewBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    useEffect(() => {
        const el = previewAreaRef.current;
        if (!el) return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            // 扣掉 padding，避免 maxWidth 把圖塞進右側保留給 StampToolbar 的 pr-0 lg:pr-14 區域而被遮住
            const cs = window.getComputedStyle(el);
            const pl = parseFloat(cs.paddingLeft) || 0;
            const pr = parseFloat(cs.paddingRight) || 0;
            const pt = parseFloat(cs.paddingTop) || 0;
            const pb = parseFloat(cs.paddingBottom) || 0;
            setPreviewBox({ w: rect.width - pl - pr, h: rect.height - pt - pb });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener('resize', update);
        return () => { ro.disconnect(); window.removeEventListener('resize', update); };
    }, []);
    const { styles, ratios, tones, toolkits } = useReferenceData(['styles', 'ratios', 'tones', 'toolkits']);
    const [allFlowModels, setAllFlowModels] = useState<FlowModel[]>([]);
    useEffect(() => { fetchFlowModels().then(setAllFlowModels).catch(console.error); }, []);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [editDraft, setEditDraft] = useState<EditDraft>(() => makeEditDraft(genConfig, flowModel));

    // 內嵌印章/畫筆編輯
    const { stamps, addStamp, updateStamp, removeStamp, clearStamps } = useStamps();
    const drawCanvas = useDrawCanvas();
    const isMobile = useIsMobile();
    const [isPaintMode, setIsPaintMode] = useState(false);
    const eyedropperCallbackRef = useRef<((color: string) => void) | null>(null);

    // 切換歷史圖時清空已加的印章/筆跡
    useEffect(() => {
        clearStamps();
        drawCanvas.clearAll();
        if (drawCanvas.canvasRef.current) {
            drawCanvas.canvasRef.current.width = 0;
            drawCanvas.canvasRef.current.height = 0;
        }
        setIsPaintMode(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode2Result?.imageUrl]);

    // 進入畫筆模式時同步 canvas 尺寸
    useEffect(() => {
        if (!isPaintMode || !imageContainerRef.current || !drawCanvas.canvasRef.current) return;
        const canvas = drawCanvas.canvasRef.current;
        if (canvas.width > 0) return;
        const container = imageContainerRef.current;
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;
    }, [isPaintMode]);

    // 滴管取色：點擊圖片區域擷取像素色
    const handleEyedropperClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
        if (drawCanvas.tool !== 'eyedropper' || !mode2Result) return;
        const container = imageContainerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        try {
            const res = await fetch(getProxyUrl(mode2Result.imageUrl), { credentials: 'include' });
            const blob = await res.blob();
            const bitmap = await createImageBitmap(blob);
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = 1;
            tempCanvas.height = 1;
            const ctx = tempCanvas.getContext('2d')!;
            const scaleX = bitmap.width / container.offsetWidth;
            const scaleY = bitmap.height / container.offsetHeight;
            ctx.drawImage(bitmap, -clickX * scaleX, -clickY * scaleY, bitmap.width, bitmap.height);
            const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
            const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            if (eyedropperCallbackRef.current) {
                eyedropperCallbackRef.current(hex);
                eyedropperCallbackRef.current = null;
            } else {
                drawCanvas.setColor(hex);
            }
            drawCanvas.setTool('brush');
        } catch {
            console.error('滴管取色失敗');
        }
    }, [drawCanvas, mode2Result]);

    const handleRequestEyedropper = useCallback((callback: (color: string) => void) => {
        eyedropperCallbackRef.current = callback;
        drawCanvas.setTool('eyedropper');
    }, [drawCanvas]);

    // 將原圖 + 筆跡 + 印章合成；儲存時用
    const composeImage = async (): Promise<Blob> => {
        if (!mode2Result) throw new Error('no mode2Result');
        // 優先用預抓好的原圖，省掉一次網路往返 —— 合成愈快，
        // iOS 的 user activation 就愈有機會撐到 navigator.share() 被呼叫
        const blob = takeOriginalBlob() ?? await fetchImageBlob(mode2Result.imageUrl);
        const imgBitmap = await createImageBitmap(blob);

        const canvas = document.createElement('canvas');
        canvas.width = imgBitmap.width;
        canvas.height = imgBitmap.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(imgBitmap, 0, 0);

        // 畫筆層
        const drawingCanvas = drawCanvas.canvasRef.current;
        if (drawingCanvas && drawCanvas.historyLength > 0 && drawingCanvas.width > 0 && drawingCanvas.height > 0) {
            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(drawingCanvas, 0, 0, imgBitmap.width, imgBitmap.height);
        }

        const container = imageContainerRef.current;
        const containerW = container?.offsetWidth ?? imgBitmap.width;
        const containerH = container?.offsetHeight ?? imgBitmap.height;
        const scaleX = imgBitmap.width / containerW;
        const scaleY = imgBitmap.height / containerH;

        for (const stamp of stamps) {
            if (!stamp.imageDataUrl) continue;
            const stampPxLeft = (stamp.xPct / 100) * containerW * scaleX;
            const stampPxTop = (stamp.yPct / 100) * containerH * scaleY;
            const stampPxW = (stamp.sizePct / 100) * containerW * scaleX;
            const hasExplicitHeight = stamp.heightPct !== undefined;
            const stampPxHExplicit = hasExplicitHeight ? (stamp.heightPct! / 100) * containerH * scaleY : 0;

            if (stamp.shapeMeta) {
                const { kind, color, lineWidth, filled } = stamp.shapeMeta;
                const h = hasExplicitHeight ? stampPxHExplicit : stampPxW;
                drawShapeOnCanvas(ctx, kind, color, lineWidth, filled, stampPxLeft, stampPxTop, stampPxW, h);
                continue;
            }
            await new Promise<void>(resolve => {
                const img = new Image();
                img.onload = () => {
                    const stampPxH = hasExplicitHeight ? stampPxHExplicit : stampPxW * (img.naturalHeight / img.naturalWidth);
                    ctx.drawImage(img, stampPxLeft, stampPxTop, stampPxW, stampPxH);
                    resolve();
                };
                img.onerror = () => resolve();
                img.src = stamp.imageDataUrl!;
            });
        }

        const resultBlob = await new Promise<Blob | null>(resolve => {
            canvas.toBlob(resolve, 'image/jpeg', 0.95);
        });
        if (!resultBlob) throw new Error('Canvas 轉換失敗');
        return resultBlob;
    };

    // Block refresh / tab close while on result page
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    // Re-sync editDraft when genConfig changes (new generation result)
    useEffect(() => {
        if (isMode2V2) {
            setEditDraft(makeEditDraft(genConfig, flowModel));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMode2V2]);

    // Summary chips — derived dynamically from flowModel + editDraft
    const summaryChips = flowModel.questions
        .filter(q => q.type !== 'image')
        .map(q => {
            const val = editDraft.answers[q.id];
            if (val === undefined || val === '' || val === false) return null;
            if (q.type === 'boolean') return q.title;
            if (q.id === 'imageAspectRatio') return ratios.find(r => r.id === val)?.name || String(val);
            if (q.id === 'imageStyle') return styles.find(s => s.id === val)?.name || String(val);
            if (q.id === 'textToneInImage') return tones.find(t => t.id === val)?.name || String(val);
            if (q.options && q.options.length > 0) return q.options.find(o => o.value === val)?.label || String(val);
            if (typeof val === 'string' && val.length > 15) return val.slice(0, 15) + '…';
            return String(val);
        })
        .filter(Boolean)
        .slice(0, 3) as string[];

    /** 儲存結果圖：有印章／筆跡時先合成。iOS 走系統分享表單（可存進照片庫），其餘平台直接下載 */
    const handleSave = async () => {
        if (!mode2Result) return;
        setIsDownloading(true);
        try {
            const hasEdits = stamps.length > 0 || drawCanvas.historyLength > 0;
            const blob = hasEdits
                ? await composeImage()
                : (takeOriginalBlob() ?? await fetchImageBlob(mode2Result.imageUrl)); // 預抓命中時同步取得
            await saveImage(blob, `jodoni-${Date.now()}${hasEdits ? '-edited' : ''}.jpg`, '里務宣傳圖片');
        } catch (error) {
            console.error('圖片儲存失敗：', error);
            showToast('圖片儲存失敗，請稍後再試');
        } finally {
            setIsDownloading(false);
        }
    };
    // ── Mode 2 V2 Layout ────────────────────────────────────────────────────
        return (
            <div
                className="flex gap-3 animate-in fade-in duration-500 min-h-[560px] lg:h-[calc(100vh-110px)]"
            >
                {/* ── Main grid: Left image column + Right chat column ── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 flex-1 min-w-0">

                {/* ── Left: Image + Buttons + Caption ── overflow-visible 才能讓 toolbar popover 向右延伸蓋到聊天區 */}
                <div className="flex flex-col gap-4 min-h-0 overflow-visible relative">
                    <div className="flex items-center gap-3 shrink-0 pr-0 lg:pr-14">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white shrink-0">生成圖片預覽</h3>
                        <div className="flex-1" />
                        {mode2Result && !isMode2Generating && (
                            <button
                                onClick={() => { setIsDislikeOpen(true); setDislikeReason(''); }}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xs font-medium rounded-xl transition-all shrink-0"
                            >
                                <ThumbsDown size={12} />
                                不喜歡，重新生成
                            </button>
                        )}
                    </div>

                    {/* 還原提示 — 讓使用者知道這是從圖片庫回來的內容，不是剛生成的 */}
                    {restoredInfo && (
                        <div className="shrink-0 rounded-xl border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 space-y-1">
                            <p>
                                此內容由圖片庫還原，原生成時間：
                                {new Date(restoredInfo.originalCreatedAt).toLocaleString('zh-TW')}
                            </p>
                            {/* 「工具包已不存在」與「題目被改過」是兩種情況，提示語不可混用 */}
                            {restoredInfo.toolkitMissing && (
                                <p>當初使用的工具包已不存在，設定可能對不上。</p>
                            )}
                            {restoredInfo.toolkitChanged && (
                                <p>工具包題目已變更，設定可能對不上。</p>
                            )}
                            {restoredInfo.orphanedAnswers.length > 0 && (
                                <p>
                                    有 {restoredInfo.orphanedAnswers.length} 項當初的設定找不到對應題目，未套用。
                                </p>
                            )}
                        </div>
                    )}

                    {/* Image preview area — flex-1 撐滿剩餘垂直空間，圖片以最長邊貼齊容器（不論橫幅 / 直幅都能完整顯示） */}
                    <div ref={previewAreaRef} className="flex-1 min-h-0 flex items-center justify-center overflow-hidden pr-0 lg:pr-14">
                        <div
                            ref={imageContainerRef}
                            className="relative shadow-xl overflow-hidden border border-slate-200 dark:border-slate-700"
                            onClick={handleEyedropperClick}
                            style={{ cursor: drawCanvas.tool === 'eyedropper' ? 'crosshair' : 'default' }}
                        >
                            <div ref={previewRef}>
                                <img
                                    src={mode2Result ? mode2Result.imageUrl : ''}
                                    className="block select-none"
                                    draggable={false}
                                    style={{
                                        display: 'block',
                                        maxWidth: previewBox.w ? `${previewBox.w}px` : '100%',
                                        maxHeight: previewBox.h ? `${previewBox.h}px` : '100%',
                                        width: 'auto',
                                        height: 'auto',
                                        // 觸控裝置：img 不接收 pointer 事件，避免攔截滴管/印章互動，並關閉 iOS 長按預覽
                                        pointerEvents: 'none',
                                        WebkitTouchCallout: 'none',
                                        WebkitUserSelect: 'none',
                                    }}
                                    alt="Generated"
                                />
                            </div>
                            <GeneratingOverlay show={isMode2Generating} />
                            <StampOverlay
                                stamps={stamps}
                                containerRef={imageContainerRef}
                                onUpdate={updateStamp}
                                onRemove={removeStamp}
                                onRequestEyedropper={handleRequestEyedropper}
                                popoverPlacement="right"
                            />
                            <DrawCanvas
                                canvasRef={drawCanvas.canvasRef}
                                tool={drawCanvas.tool}
                                containerRef={imageContainerRef}
                                onStartDrawing={drawCanvas.startDrawing}
                                onContinueDrawing={drawCanvas.continueDrawing}
                                onEndDrawing={drawCanvas.endDrawing}
                                active={isPaintMode}
                            />
                        </div>
                    </div>{/* end image preview area */}

                    {/* Dislike modal */}
                    {isDislikeOpen && (
                        <div
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
                            onClick={() => setIsDislikeOpen(false)}
                        >
                            <div
                                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-5 w-[90vw] max-w-sm flex flex-col gap-4"
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-bold text-slate-800 dark:text-white text-sm">告訴我哪裡不滿意</span>
                                    <button onClick={() => setIsDislikeOpen(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors">
                                        <X size={16} />
                                    </button>
                                </div>
                                <textarea
                                    autoFocus
                                    value={dislikeReason}
                                    onChange={e => setDislikeReason(e.target.value)}
                                    placeholder="例如：顏色太暗、人物表情不自然、背景太複雜..."
                                    rows={3}
                                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                />
                                <button
                                    disabled={!dislikeReason.trim()}
                                    onClick={() => {
                                        onRegenerateWithConfig?.({
                                            flowModelId: editDraft.flowModelId,
                                            answers: editDraft.answers,
                                            additionalRequirements: `請重新生成一張不一樣的，不喜歡的原因：${dislikeReason.trim()}`,
                                        });
                                        setIsDislikeOpen(false);
                                    }}
                                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-all"
                                >
                                    重新生成
                                </button>
                            </div>
                        </div>
                    )}

                    {/* StampToolbar — 桌面絕對定位釘在左欄右側、手機改 inline 橫向放在圖片下方 */}
                    {mode2Result && !isMode2Generating && (
                        <div className="lg:absolute lg:right-0 lg:top-12 lg:z-10 flex justify-center lg:block max-w-full">
                            <StampToolbar
                                onAddStamp={addStamp}
                                onTogglePaint={() => setIsPaintMode(v => !v)}
                                isPaintMode={isPaintMode}
                                draw={drawCanvas}
                                onRequestEyedropper={handleRequestEyedropper}
                                popoverPlacement={isMobile ? 'bottom' : 'right'}
                                orientation={isMobile ? 'horizontal' : 'vertical'}
                            />
                        </div>
                    )}

                    <ImageHistoryStrip
                        history={imageHistory}
                        selectedIndex={selectedHistoryIndex}
                        onSelect={onSelectHistoryImage ?? (() => {})}
                    />

                    <CaptionPanel
                        mode2Result={mode2Result}
                        genConfig={genConfig}
                        name={name}
                        title={title}
                        organizationName={organizationName}
                        organizationType={organizationType}
                        variant="compact"
                        actionButton={
                            <button
                                onClick={() => void handleSave()}
                                disabled={isDownloading}
                                className={`flex-1 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-950 rounded-xl font-bold hover:bg-slate-700 dark:hover:bg-blue-50 transition-all flex items-center justify-center gap-2 text-sm ${isDownloading ? 'opacity-70 cursor-wait' : ''}`}
                            >
                                {isDownloading
                                    ? <><div className="animate-spin h-4 w-4 border-2 border-white dark:border-slate-950 border-t-transparent rounded-full" /> 處理中...</>
                                    : <><Download size={15} /> {canSaveToPhotos() ? '儲存圖片' : '下載圖片'}</>}
                            </button>
                        }
                    />
                </div>

                {/* ── Right: Settings (collapsible) + Chat (fills rest) ── */}
                <div className="flex flex-col gap-3 min-h-[480px] lg:min-h-0 overflow-hidden">
                    {/* Settings Summary — collapsible */}
                    <section className="bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl shrink-0">
                        {/* Header row — always visible, click to toggle */}
                        <button
                            className="w-full flex items-center justify-between px-4 py-3 gap-3"
                            onClick={() => {
                                if (isEditMode) return;
                                setIsSettingsOpen(v => !v);
                            }}
                        >
                            <span className="font-bold text-amber-500 dark:text-amber-400 flex items-center gap-1.5 text-sm shrink-0">
                                <Sparkles size={14} /> 生成設定
                            </span>
                            {!isSettingsOpen && !isEditMode && (
                                <div className="flex flex-wrap gap-1 flex-1 justify-start">
                                    {summaryChips.map((c, i) => (
                                        <span key={i} className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full">
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="flex items-center gap-2 shrink-0">
                                {isSettingsOpen && !isEditMode && (
                                    <span
                                        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setIsEditMode(true);
                                        }}
                                    >
                                        <Pencil size={12} /> 編輯
                                    </span>
                                )}
                                {isEditMode && (
                                    <span
                                        className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                        onClick={e => { e.stopPropagation(); setIsEditMode(false); }}
                                    >
                                        <X size={12} /> 取消
                                    </span>
                                )}
                                <ChevronDown
                                    size={15}
                                    className={`text-slate-400 transition-transform duration-200 ${isSettingsOpen || isEditMode ? 'rotate-180' : ''}`}
                                />
                            </div>
                        </button>

                        {(isSettingsOpen || isEditMode) && (
                            <div className="px-4 pb-4 space-y-2 text-xs border-t border-slate-100 dark:border-slate-800 pt-3">
                                <SettingsCard
                                    isEditMode={isEditMode}
                                    editDraft={editDraft}
                                    setEditDraft={setEditDraft}
                                    flowModel={flowModel}
                                    allFlowModels={allFlowModels}
                                    styles={styles}
                                    ratios={ratios}
                                    tones={tones}
                                    toolkits={toolkits}
                                    onSaveDraft={() => {
                                        setIsEditMode(false);
                                        setIsSettingsOpen(false);
                                    }}
                                    onCancelEdit={() => {
                                        setEditDraft(makeEditDraft(genConfig, flowModel));
                                        setIsEditMode(false);
                                    }}
                                    compact={true}
                                />
                            </div>
                        )}
                    </section>

                    {/* Chat Panel — fills remaining height */}
                    <div className="flex-1 min-h-0 flex flex-col">
                        <QuickGenChatPanel
                            imageUrl={mode2Result ? mode2Result.imageUrl : ''}
                            currentImageFileId={mode2Result?.fileId}
                            name={name}
                            title={title}
                            organizationName={organizationName}
                            organizationType={organizationType}
                            flowModelId={genConfig.flowModelId}
                            questions={flowModel.questions}
                            currentSettings={editDraft.answers}
                            onSettingChange={(field, value) => {
                                setEditDraft(d => ({ ...d, answers: { ...d.answers, [field]: value } }));
                            }}
                            onConfirmGenerate={(additionalRequirements) => {
                                onRegenerateWithCurrentImage?.({
                                    flowModelId: editDraft.flowModelId,
                                    answers: editDraft.answers,
                                    additionalRequirements,
                                });
                            }}
                            isGenerating={isMode2Generating ?? false}
                            generationFailed={isMode2GenerationFailed ?? false}
                            chatRestore={chatRestore}
                            generationSessionId={generationSessionId}
                            onChatSessionIdChange={onChatSessionIdChange}
                        />
                    </div>
                </div>
                </div>{/* end inner grid */}
            </div>
        );


};

export default Step4Result;
